<template>
  <v-navigation-drawer
    v-if="authStore.isAuthenticated"
    v-model="drawer"
    temporary
    color="background"
    elevation="2"
    width="280"
  >
    <v-list density="compact" nav>
      <v-list-item
        prepend-icon="mdi-home"
        :title="$t('nav.home')"
        value="home"
        to="/"
        exact
        @click="closeDrawer"
      />
      
      <v-list-item
        prepend-icon="mdi-account"
        :title="$t('profile.title')"
        value="profile"
        to="/profile"
        @click="closeDrawer"
      />
      
      <v-list-item
        v-if="imagesEnabled"
        prepend-icon="mdi-image"
        title="Images"
        value="images"
        to="/images"
        @click="closeDrawer"
      />

      <v-list-item
        v-if="videosEnabled"
        prepend-icon="mdi-video"
        title="Videos"
        value="videos"
        to="/videos"
        @click="closeDrawer"
      />

      <v-list-item
        v-if="imagesEnabled"
        prepend-icon="mdi-folder-multiple-image"
        title="Assets"
        value="assets"
        to="/assets"
        @click="closeDrawer"
      />
      
      <v-list-item
        prepend-icon="mdi-television-play"
        title="Game Shows"
        value="gameshows"
        to="/gameshows"
        @click="closeDrawer"
      />

      <v-list-item
        prepend-icon="mdi-swap-horizontal-bold"
        :title="$t('dataTransfer.title')"
        value="data-transfer"
        to="/data-transfer"
        @click="closeDrawer"
      />
      
      <template v-if="authStore.isAdmin">
        <v-divider class="my-2" />
        
        <v-list-subheader>{{ $t('nav.admin') }}</v-list-subheader>
        
        <v-list-item
          prepend-icon="mdi-account-group"
          :title="$t('users.title')"
          value="users"
          to="/users"
          @click="closeDrawer"
        />
        
        <v-list-item
          prepend-icon="mdi-cog"
          :title="$t('nav.config')"
          value="admin-config"
          to="/admin/config"
          @click="closeDrawer"
        />
        
        <v-list-item
          prepend-icon="mdi-security"
          :title="$t('admin.authTools.title')"
          value="admin-auth-tools"
          to="/admin/auth-tools"
          @click="closeDrawer"
        />
        
        <v-list-item
          prepend-icon="mdi-database"
          :title="$t('admin.database.title')"
          value="admin-database"
          to="/admin/database"
          @click="closeDrawer"
        />

        <v-list-item
          prepend-icon="mdi-cash-100"
          :title="$t('nav.usage')"
          value="admin-usage"
          to="/admin/usage"
          @click="closeDrawer"
        />

        <v-list-item
          prepend-icon="mdi-robot-outline"
          :title="$t('nav.providers')"
          value="admin-providers"
          to="/admin/providers"
          @click="closeDrawer"
        />
        
        <v-divider class="my-2" />
        
        <v-list-subheader>Quiz Management</v-list-subheader>
        
        <v-list-item
          prepend-icon="mdi-gamepad-variant"
          title="Games"
          value="games"
          to="/games"
          @click="closeDrawer"
        />
      </template>
    </v-list>
    
    <template v-slot:append>
      <div class="pa-2">
        <v-list density="compact" nav>
          <v-list-item
            prepend-icon="mdi-logout"
            :title="$t('auth.logout')"
            @click="logout"
          />
        </v-list>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { onMounted, ref, watch } from 'vue'
import { useImagesStore } from '@/store/images'
import { videosApi } from '@/services/videosApi'

const router = useRouter()
const authStore = useAuthStore()
const imagesStore = useImagesStore()
const imagesEnabled = ref(false)
const videosEnabled = ref(false)

// Expose drawer model for parent component
const drawer = defineModel<boolean>('drawer', { default: false })

const closeDrawer = () => {
  drawer.value = false
}

const logout = () => {
  authStore.logout()
  closeDrawer()
  router.push('/login')
}

const refreshImagesStatus = async () => {
  if (!authStore.isAuthenticated) { imagesEnabled.value = false; return }
  await imagesStore.fetchStatus()
  imagesEnabled.value = imagesStore.enabled
}

const refreshVideosStatus = async () => {
  if (!authStore.isAuthenticated) { videosEnabled.value = false; return }
  try {
    const response = await videosApi.status()
    videosEnabled.value = response.data?.enabled || false
  } catch {
    videosEnabled.value = false
  }
}

const refreshStatus = async () => {
  await refreshImagesStatus()
  await refreshVideosStatus()
}

onMounted(refreshStatus)
watch(() => authStore.isAuthenticated, () => refreshStatus())
</script>
